import { useCallback, useMemo } from "react";

import usePersistedState from "hooks/usePersistedState";

import { PinnedItemType } from "./pinnedItemsTypes";

type PinnedItemsState = {
  [K in PinnedItemType]?: string[];
};

export const usePinnedItems = (itemType: PinnedItemType) => {
  const [pinnedItemsState, setPinnedItemsState] =
    usePersistedState<PinnedItemsState>(
      "pinned-items-v1",
      {},
      {
        loginDependent: false,
        accountDependent: true,
      }
    );

  const pinnedItems = useMemo(
    () => pinnedItemsState[itemType] || [],
    [pinnedItemsState, itemType]
  );

  const addPinnedItem = useCallback(
    (itemId: string) => {
      const currentItems = pinnedItemsState[itemType] || [];
      if (!currentItems.includes(itemId)) {
        setPinnedItemsState({
          ...pinnedItemsState,
          [itemType]: [...currentItems, itemId],
        });
      }
    },
    [pinnedItemsState, itemType, setPinnedItemsState]
  );

  const removePinnedItem = useCallback(
    (itemId: string) => {
      const currentItems = pinnedItemsState[itemType] || [];
      setPinnedItemsState({
        ...pinnedItemsState,
        [itemType]: currentItems.filter(id => id !== itemId),
      });
    },
    [pinnedItemsState, itemType, setPinnedItemsState]
  );

  const togglePinned = useCallback(
    (itemId: string) => {
      if (pinnedItems.includes(itemId)) {
        removePinnedItem(itemId);
      } else {
        addPinnedItem(itemId);
      }
    },
    [pinnedItems, removePinnedItem, addPinnedItem]
  );

  return {
    pinnedItems,
    addPinnedItem,
    removePinnedItem,
    togglePinned,
  };
};
